
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { formatPhoneNumberToNational, formatPhoneNumberToInternational } from '@/utils/format';
import { Validate } from '@/types';
import '@/validation-rules';
import Tooltip from '@/components/shared/Tooltip.vue';

const namespace: string = 'cart';

@Component<MembershipProgramForm>({
	components: {
		ValidationObserver,
		ValidationProvider,
		Tooltip
	}
})

export default class MembershipProgramForm extends Vue {
	@Prop({ type: Object, required: true, default: () => {} }) private membershipCustomization!: MembershipCustomization;
	@Action('updateContactInformation', { namespace }) private updateContactInformation!: (contact: CheckoutContactInfo) => void;
	@Action('checkIfMemberAndApplyPromo', { namespace: 'auth' }) private checkIfMemberAndApplyPromo!: (payload: { identifier: string, discount?: MemberPromo | PunchhPromo }) => void;
	@Getter('getContactInformation', { namespace }) private contactInformation!: CheckoutContactInfo;
	@Getter('getMemberInfo', { namespace }) private memberInfo!: CheckoutMemberInfo | null;
	@Getter('membershipAppliedSuccessfully', { namespace }) private membershipAppliedSuccessfully!: boolean;
	@Watch('contactInformation', { immediate: true, deep: true })
	onContactInformationChanged() {
		if(this.contactInformation) {
			if(this.membershipCustomization.input_type !== 'account') {
				const value = this.contactInformation[this.membershipCustomization.input_type];
				if(value) {
					if(this.membershipCustomization.input_type === 'phone_number') {
						// The store holds phone numbers in E.164; this form works in national
						// format. Skip the echo of our own write-back so the input is not
						// rewritten mid-typing.
						if(value !== this.lastWrittenPhoneNumber) {
							this.identifier = formatPhoneNumberToNational(value);
						}
					}
					else {
						this.identifier = value;
					}
				}
			}
		}
	};
	private opened: boolean = true;
	private loading: boolean = false;
	private identifier: string = '';
	private lastWrittenPhoneNumber: string | null = null;
	private inputType: string = '';
	private inputRules: string = '';
	private inputMaxLength: string = '';
	private inputErrorMsg: string = '';

	$refs!: { observer: Validate };

	/**
	 * Get title before last word
	 *
	 * @return {string}
	 */
	private get titleBeforeLastWord(): string {
		const title = this.membershipCustomization.title.trim();
		const lastSpaceIndex = title.lastIndexOf(' ');
		return lastSpaceIndex > -1 ? title.substring(0, lastSpaceIndex + 1) : '';
	}

	/**
	 * Get last word of title
	 * This is to prevent the tooltip icon from breaking into a new line by itself
	 *
	 * @return {string}
	 */
	private get titleLastWord(): string {
		const title = this.membershipCustomization.title.trim();
		const lastSpaceIndex = title.lastIndexOf(' ');
		return lastSpaceIndex > -1 ? title.substring(lastSpaceIndex + 1) : title;
	}

	private created(): void {
		this.identifier = this.memberInfo?.identifier || '';

		if (!this.identifier && this.membershipCustomization.input_type !== 'account') {
			const value = this.contactInformation[this.membershipCustomization.input_type];
			this.identifier = this.membershipCustomization.input_type === 'phone_number' ? formatPhoneNumberToNational(value) : value;
		}
	}

	/**
	 * Get input type and its rules
	 *
	 * @return {void}
	 */
	private mounted(): void {
		if(this.membershipCustomization.input_type === 'full_name') {
			this.inputType = 'text';
			this.inputRules = 'required';
			this.inputMaxLength = '40';
			this.inputErrorMsg = this.$t('menu.membership_drawer.error_invalid_name');
		}
		else if(this.membershipCustomization.input_type === 'email') {
			this.inputType = 'email';
			this.inputRules = 'required|email';
			this.inputMaxLength = '40';
			this.inputErrorMsg = this.$t('menu.membership_drawer.error_invalid_email');
		}
		else if(this.membershipCustomization.input_type === 'phone_number') {
			this.inputType = 'tel';
			this.inputRules = 'required|phone';
			this.inputMaxLength = '15';
			this.inputErrorMsg = this.$t('menu.membership_drawer.error_invalid_phone_number');
			this.formatInput();
		}
		else {
			this.inputType = 'text';
			this.inputRules = 'required';
			this.inputMaxLength = '50';
			this.inputErrorMsg = this.$t('menu.membership_drawer.error_invalid_account');
		}
	}

	/**
	 * Close the drawer
	 *
	 * @return {void}
	 */
	private closeDrawer(): void {
		this.opened = false;
	}

	/**
	 * Format the phone number using the utility function
	 *
	 * @return {void}
	 */
	private formatInput(): void {
		if(this.inputType === 'tel' && this.identifier) {
			this.identifier = formatPhoneNumberToNational(this.identifier);
		}

		if(this.membershipCustomization.input_type !== 'account') {
			this.updateContactInfo();
		}
	}

	/**
	 * Update the contact information
	 *
	 * @return {void}
	 */
	private updateContactInfo(): void {
		const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		const isEmailInput = this.membershipCustomization.input_type === 'email';
		const isValidEmail = isEmailInput && emailRegex.test(this.identifier.toLowerCase());

		// The store holds phone numbers in E.164; convert back so the national string doesn't clobber it
		const phoneNumber = this.membershipCustomization.input_type === 'phone_number'
			? formatPhoneNumberToInternational(this.identifier)
			: this.contactInformation.phone_number;
		this.lastWrittenPhoneNumber = phoneNumber;

		this.updateContactInformation(
			{
				full_name: this.membershipCustomization.input_type === 'full_name' ? this.identifier : this.contactInformation.full_name,
				email: isEmailInput
					? isValidEmail ? this.identifier : ''
					: this.contactInformation.email,
				phone_number: phoneNumber,
				password: this.contactInformation.password
			}
		);
	}

	/**
	 * Check and apply the membership promo and special menus
	 *
	 * @return {void}
	 */
	private async checkAndApplyMembershipPromo(): Promise<void> {
		try {
			this.loading = true;
			let isValid = await this.$refs.observer.validate();
			if (isValid) {
				await this.checkIfMemberAndApplyPromo({ identifier: this.inputType === 'tel' ? this.identifier.replace(/[^0-9]/g, '') : this.identifier });
				this.$emit('membership-applied');
				setTimeout(() => {
					this.opened = false;
				}, 150);
			}

			// Invalid phone number (We do not have room here to display an error message
			// below the field so we show a toast)
			else {
				this.$toasted.show(this.inputErrorMsg, { type: 'error', position: 'top-center' }).goAway(5000);
			}
		}
		catch(error) {
			this.$toasted.show(this.$t('menu.membership_drawer.error_profile_not_found'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading = false;
		}
	}
}
